'use client';

import { checkUrl } from '@hc-frontend/core-utils-url';
import NextLink from 'next/link';
import { redirect } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useEventBus } from '../../hooks/use-event-bus/use-event-bus';
import { useTrackingContexts } from '../../hooks/use-tracking-contexts/use-tracking-contexts';
import type { LinkLoadEvent, LinkProps } from './link.types';
import {
  checkHealthcareComLink,
  checkInsxCloudComLink,
  checkMedicareComLink,
  checkPivothealthComLink,
  checkRxCardsComLink,
  checkStmHealthcareComLink,
  isHealthcareComLink,
  isInsxCloudComLink,
  isMedicareComLink,
  isPivothealthComLink,
  isRxCardsComLink,
  isStmHealthcareComLink,
} from './link-checks';

const checkers = [
  [isHealthcareComLink, checkHealthcareComLink],
  [isInsxCloudComLink, checkInsxCloudComLink],
  [isMedicareComLink, checkMedicareComLink],
  [isStmHealthcareComLink, checkStmHealthcareComLink],
  [isPivothealthComLink, checkPivothealthComLink],
  [isRxCardsComLink, checkRxCardsComLink],
];

const getOverrideHref = async (originalURL: URL) => {
  const checker = checkers.find(([match]) => match(originalURL));
  let updatedHref: string | undefined;
  if (checker) updatedHref = (await checker[1](originalURL)) as string;

  return updatedHref ?? originalURL.href;
};

const getTrackingHref = (href: string) => {
  if (href.startsWith('/')) {
    return `${window.location.origin}${href}`;
  }

  return href;
};

export function Link({
  children,
  id,
  onClick,
  href,
  prefetch: _,
  placement,
  category,
  label,
  ...props
}: LinkProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const ref = useRef<HTMLAnchorElement>(null);

  const [internalHref, setInternalHref] = useState<string>(href);
  const [trackingHref, setTrackingHref] = useState<string>(href);

  useEffect(() => {
    if (ref.current) {
      const url = checkUrl(href);
      if (url) {
        getOverrideHref(url).then((actualHref) => {
          setInternalHref(actualHref);
        });
      } else {
        setInternalHref(href);
      }

      const newTrackingHref = getTrackingHref(href);
      setTrackingHref(newTrackingHref);

      eventBus.publish<LinkLoadEvent>({
        id,
        topic: 'core.ui.link.load',
        href: newTrackingHref,
        source: ref.current,
      });
    }
  }, [id, eventBus, href, setInternalHref, setTrackingHref]);

  return (
    <NextLink
      ref={ref}
      href={internalHref}
      onClick={(e) => {
        eventBus.publish({
          id,
          topic: 'core.ui.link.click',
          href: trackingHref,
          source: e.currentTarget,
          contexts,
          placement,
          category,
          label,
        });

        onClick?.(e);

        /**
         * TEMP: Remove this once we have a better solution for external links
         *
         * The reasoning behind this is that we want to prevent the default behavior
         * of prefetching the page when the user clicks on an external link. This is not
         * the desired behavior as we're acting as a proxy for all other HC pages.
         */
        if (!href.startsWith('/')) {
          redirect(href);
        }
      }}
      {...props}
      prefetch={false}
    >
      {children}
    </NextLink>
  );
}
